import { CSSProperties, RefObject } from 'react';
import { DragSourceHookSpec } from 'react-dnd';

import { CurrencyCodeType } from '@mint-lib/currencies-helpers';
import type { useChannels } from '@mint-lib/hooks';

import { Campaign } from '../../types/_campaigns/campaigns.js';
import { Platform } from '../../types/integrations.js';
import type { TRACKING_EVENTS } from '../../types/performanceTracking.js';

export type ModalOpenedFor = {
  buttonRef: RefObject<HTMLDivElement>;
  id: number | string;
  rowId: string;
  parentRowId: number;
  type: string;
  channelName: string;
  onSubmitAddRow: (
    id: number,
    isSameLevelRow: boolean,
    rowObject: any,
    rowParentId: number,
  ) => void;
};

export type RowToNotificationObject = {
  rowId: number;
  columnId: number | string;
};

export type RowToRemoveObject = {
  rowId: number;
  hasChildren: boolean;
  type: string;
  campaign?: number | null;
};

export type RowToChangeCurrencyObject = {
  rowId: number;
  rowName: string;
  rowCurrency: CurrencyCodeType | null;
  startDate: string;
  endDate: string;
};

export type ColumnOptionItem = {
  value: string;
  displayName: string;
  isDeleted?: boolean;
  isDisabled?: boolean;
};

export enum SETTINGS_STATUS {
  WARNING = 'warning',
  REMINDER = 'reminder',
  EMPTY = 'empty',
  NO_CAMPAIGNS = 'no_campaigns_info',
}

export type BuilderTableRow = {
  id: number;
  idx?: number;
  depth: number;
  cells: Array<any>;
  getRowProps: (props?: { style: CSSProperties }) => any;
  index?: string;
  original: {
    costPerMetricInLocalCurrency?: string | number | null;
    optimalBudgetInLocalCurrency?: string | number | null;
    order?: number;
    channel?: string;
    platformMedia?: string;
    budget?: string;
    currencySign: string;
    currencyValue: CurrencyCodeType | null;
    id: number;
    isGhost?: boolean;
    type: string;
    campaign?: number;
    isFromRecommendations: boolean;
    isLockedForRecommendations?: boolean;
    campaignMediaPlanItemId?: number;
    settingsStatusMessage: SETTINGS_STATUS;
    supportNotifications: boolean;
    icon?: string;
    parent: number;
    subRows: Array<{ id: number }>;
    parentInfo: {
      name: string;
      dateFrom?: string;
      dateTo?: string;
      isGhost?: boolean;
    };
    dateFrom?: string;
    dateTo?: string;
    nameKey: string;
    isConfigured?: boolean;
    label?: string;
    logo?: string;
    isCopy?: boolean;
    canBeDeleted?: boolean;
    canVatBeMadeCalculated?: boolean;
    canAdserverCostBeMadeCalculated?: boolean;
    name?: string;
    hasIntegration?: boolean;
    isConfiguredForIo?: boolean;
    isBuyingPlanLocked: boolean;
    isBuyingPlanApproved: boolean;
    isCampaignCurrencyMode: boolean;
    isPredictivePlanningMode: boolean;
    isArchivedVersion: boolean;
    isEditMode: boolean;
    isValidIoGroup: boolean;
    ioVersionIndex?: number | null;
    userIoGroupId: number | null;
    integratedPlatforms: Platform[];
    platformByName: Record<string, Platform>;
    channelsMeta: ReturnType<typeof useChannels>;
    lastRowOfChannelIds: number[];
    scale: number;
    selectedPlanId: number | null;
  };
  canExpand: boolean;
  isExpanded: boolean;
  subRows?: BuilderTableRow[];
  getToggleRowExpandedProps: (props?: { title: string | null }) => any;
  toggleRowExpanded: () => void;
  state?: { isNewAdded?: boolean };
  setState?: any;
};

export type BuilderTableColumn = {
  id: string;
  decimals?: number;
  updateRow: (
    row: { id: number },
    field: string,
    value: any,
  ) => Promise<{ isError: boolean }>;
  onRemoveClick: (rowObj: RowToRemoveObject) => void;
  onChangeRowCurrencyClick: (rowObj: RowToChangeCurrencyObject) => void;
  onAddClick: (openedFor: ModalOpenedFor | null) => void;
  onSubmitAddRow: (
    id: number,
    isSameLevelRow: boolean,
    rowObject: any,
    rowParentId: number,
  ) => void;
  startMeasurePerformance: (eventName: TRACKING_EVENTS) => void;
  options: ColumnOptionItem[];
  isInitialOpened: boolean;
  campaign: Campaign;
  width: string | number;
  // TODO: rethink the type of this property
  navigate?: any;
  isReadOnly?: boolean;
  onlyTileEditable?: boolean;
  isPercentField?: boolean;
  isCurrency?: boolean;
  Header?: string;
  isTimeRange?: boolean;
  isCalculatedSelect?: boolean;
  hideCurrencyForEmptyValue?: boolean;
  hideEmptyValueForReadOnlyRowLevel?: boolean;
  isPredictiveColumn?: boolean;
  hideColumnForNonFinancialUser?: boolean;
  hideForNonIntegratedUser?: boolean;
  fixedCell?: boolean;
  isCalculatedTechCostColumn?: boolean;
};

export type DraggableItemType = DragSourceHookSpec<any, any, any> & {
  position: number;
  canExpand: boolean;
  name: string;
  id: number;
  width?: string;
  original?: {
    type: string;
    parent: number;
    canBeDeleted: boolean;
    campaign: number | null;
    subRows?: { id: string; name: string }[] | null;
    order: number;
  };
  values?: { name: string };
  rowType?: string;
  channelName?: string;
};

export type ChannelRowType = {
  name: string;
  label: string;
  platforms?: Platform[];
};

type Row = {
  budget: string | null;
  canBeDeleted: boolean;
  currencySign: string;
  dateFrom: string;
  dateTo: string;
  errors?: string;
  fields: null | string[];
  id: number;
  isCopy: null | boolean;
  label: string;
  name: string;
  nameKey: string;
  optimalBudget?: string;
  type: string;
  campaign?: number;
  parent?: number;
};
export type BuilderRowType = Row & { subRows?: Row[] };
